import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { EMPLOYMENT_TYPES, employmentTypeLabel } from "@/lib/constants";
import PublicNav from "@/components/PublicNav";
import { MapPin, Search, Briefcase, Laptop } from "lucide-react";

export default function Jobs() {
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [remote, setRemote] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (country) params.country = country;
      if (city) params.city = city;
      if (employmentType) params.employment_type = employmentType;
      if (remote) params.remote = true;
      const res = await api.get("/offers", { params });
      setOffers(res.data);
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [q, country, city, employmentType, remote]);

  useEffect(() => {
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [search]);

  const inputCls =
    "border border-slate-200 rounded-lg px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent";

  return (
    <div data-testid="jobs-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-3xl font-bold text-white mb-2">Trouver un emploi</h1>
          <p className="text-white/60 mb-8">Des opportunités internationales vous attendent.</p>
          <form
            onSubmit={(e) => { e.preventDefault(); search(); }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
          >
            <input
              data-testid="jobs-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Mot-clé, métier, entreprise…"
              className={`${inputCls} lg:col-span-2`}
            />
            <input
              data-testid="jobs-country-input"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Pays"
              className={inputCls}
            />
            <input
              data-testid="jobs-city-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ville"
              className={inputCls}
            />
            <button
              data-testid="jobs-search-button"
              type="submit"
              className="flex items-center justify-center gap-2 bg-brand-turquoise text-white font-semibold rounded-lg px-5 py-2.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
            >
              <Search className="w-4 h-4" />
              Rechercher
            </button>
          </form>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <select
              data-testid="jobs-type-select"
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value)}
              className={`${inputCls} text-sm`}
            >
              <option value="">Tous les types</option>
              {EMPLOYMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
              <input
                data-testid="jobs-remote-checkbox"
                type="checkbox"
                checked={remote}
                onChange={(e) => setRemote(e.target.checked)}
                className="w-4 h-4 accent-brand-turquoise"
              />
              Travail à distance
            </label>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <p data-testid="jobs-count" className="text-sm text-slate-500 mb-6">
          {loading ? "Recherche en cours…" : `${offers.length} offre(s) trouvée(s)`}
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {offers.map((o) => (
            <Link
              key={o.offer_id}
              to={`/jobs/${o.offer_id}`}
              data-testid={`job-card-${o.offer_id}`}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:border-brand-turquoise hover:shadow-md transition-all duration-200 group"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="font-bold text-brand-navy text-lg group-hover:text-brand-turquoise transition-colors">
                  {o.title}
                </h2>
                {o.remote && (
                  <span className="flex items-center gap-1 text-xs font-medium text-brand-blue bg-brand-blue/10 rounded-full px-2.5 py-1 shrink-0">
                    <Laptop className="w-3 h-3" /> Remote
                  </span>
                )}
              </div>
              <p className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 text-slate-400" /> {o.company}
              </p>
              <p className="text-sm text-slate-500 flex items-center gap-1.5 mb-3">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                {[o.city, o.country].filter(Boolean).join(", ")}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-brand-sky text-brand-navy rounded-full px-2.5 py-1">
                  {employmentTypeLabel(o.employment_type)}
                </span>
                {o.salary && (
                  <span className="text-xs bg-brand-turquoise/10 text-brand-turquoise rounded-full px-2.5 py-1 font-medium">
                    {o.salary} {o.salary_currency || ""}
                  </span>
                )}
                {o.sector && (
                  <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">{o.sector}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
        {!loading && offers.length === 0 && (
          <p data-testid="jobs-empty" className="text-center text-slate-400 bg-white rounded-xl border border-slate-200 p-12">
            Aucune offre ne correspond à votre recherche.
          </p>
        )}
      </div>
    </div>
  );
}
